import { forwardRef, type ReactNode } from "react";
import { cva } from "class-variance-authority";
import { Button as BaseButton, type ButtonProps } from "components/ui/button";
import { Spinner } from "../../ui/spinner";
import Tooltip from "../Tooltip";
import { cn } from "../../../lib/utils";

interface Props extends ButtonProps {
    loading?: boolean;
    tooltip?: ReactNode;
    icon?: ReactNode;
}

// Overrides layered on top of components/ui/button.tsx's buttonVariants, keyed by the same variant.
const overrideVariants = cva("tw-flex tw-items-center tw-gap-2", {
    variants: {
        variant: {
            default: "tw-shadow-none",
            destructive: "tw-shadow-none",
            outline: "tw-shadow-none",
            secondary: "tw-shadow-none",
            ghost: "",
            link: "",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

const Button = forwardRef<HTMLButtonElement, Props>(
    ({ loading, disabled, children, className, tooltip, icon, ...props }, ref) => {
        const button = (
            <BaseButton
                {...props}
                ref={ref}
                className={cn(overrideVariants({ variant: props.variant }), className)}
                disabled={disabled || loading}
            >
                {loading ? <Spinner /> : icon}
                {children}
            </BaseButton>
        );

        if (!tooltip) {
            return button;
        }

        return (
            <Tooltip trigger={button}>
                {tooltip}
            </Tooltip>
        );
    }
);

export default Button;
